import { formatearFecha } from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { BotonEnlace, EstadoVacio, Pildora } from "../componentes/ui.tsx";
import type { FilaEncuesta } from "../tipos.ts";

const TONO_ESTADO: Record<FilaEncuesta["estado"], { nombre: string; tono: string }> = {
  borrador: { nombre: "Borrador", tono: "bg-gris-fondo text-gris" },
  abierta: { nombre: "Abierta", tono: "bg-verde-fondo text-verde" },
  cerrada: { nombre: "Cerrada", tono: "bg-marino-claro text-marino" },
};

/** Lo que me falta responder, lo que creé y, a un administrador, lo publicado por los demás. */
export function PaginaEncuestas() {
  const trpc = useTRPC();
  const lista = useQuery(trpc.encuestas.listar.queryOptions());

  return (
    <div>
      <Encabezado
        overline="Sistema"
        titulo="Encuestas"
        acciones={
          <BotonEnlace to="/encuestas/nueva" variante="primario">
            Nueva encuesta
          </BotonEnlace>
        }
      />
      {lista.isPending && <EstadoVacio>Cargando…</EstadoVacio>}
      {lista.data && (
        <div className="space-y-7">
          <Seccion titulo="Por responder">
            {lista.data.porResponder.length === 0 ? (
              <EstadoVacio>Estás al día: no tienes encuestas por responder.</EstadoVacio>
            ) : (
              <ul className="divide-y divide-borde/60 rounded-tarjeta border border-borde bg-white">
                {lista.data.porResponder.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{e.titulo}</div>
                      <div className="text-[11.5px] text-texto-3">
                        De {e.autor}
                        {e.anonima && " · anónima"}
                        {e.cierraEn && ` · cierra el ${formatearFecha(e.cierraEn)}`}
                      </div>
                    </div>
                    <BotonEnlace
                      to="/encuestas/$id"
                      params={{ id: e.id }}
                      variante="primario"
                      compacto
                    >
                      Responder
                    </BotonEnlace>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
          <Seccion titulo="Mis encuestas">
            {lista.data.mias.length === 0 ? (
              <EstadoVacio>Todavía no has creado ninguna encuesta.</EstadoVacio>
            ) : (
              <Filas encuestas={lista.data.mias} />
            )}
          </Seccion>
          {lista.data.otras.length > 0 && (
            <Seccion titulo="De otras personas">
              <Filas encuestas={lista.data.otras} />
            </Seccion>
          )}
        </div>
      )}
    </div>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 text-[15px] font-bold text-marino">{titulo}</h2>
      {children}
    </section>
  );
}

function Filas({ encuestas }: { encuestas: FilaEncuesta[] }) {
  return (
    <ul className="divide-y divide-borde/60 rounded-tarjeta border border-borde bg-white">
      {encuestas.map((e) => {
        const estado = TONO_ESTADO[e.estado];
        return (
          <li key={e.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{e.titulo}</div>
              <div className="text-[11.5px] text-texto-3">
                {e.estado === "borrador"
                  ? `Creada el ${formatearFecha(e.creadaEn)}`
                  : `Respondieron ${e.respondieron} de ${e.total}`}
                {e.anonima && " · anónima"}
                {e.estado === "abierta" &&
                  e.cierraEn &&
                  ` · cierra el ${formatearFecha(e.cierraEn)}`}
              </div>
            </div>
            <Pildora tono={estado.tono}>{estado.nombre}</Pildora>
            {e.estado === "borrador" ? (
              <BotonEnlace to="/encuestas/$id/editar" params={{ id: e.id }} compacto>
                Seguir editando
              </BotonEnlace>
            ) : (
              <BotonEnlace to="/encuestas/$id/resultados" params={{ id: e.id }} compacto>
                Ver resultados
              </BotonEnlace>
            )}
          </li>
        );
      })}
    </ul>
  );
}
